const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value, empty = '—') => toDate(value)?.toLocaleDateString() ?? empty;

export const formatDateTime = (value, empty = '—') => toDate(value)?.toLocaleString() ?? empty;

/** "12 Mar, 14:05" — the year is added only when it is not the current one. */
export const formatShortDateTime = (value, empty = '—') => {
  const date = toDate(value);
  if (!date) return empty;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  const day = date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: '2-digit' }),
  });
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time}`;
};

/** "just now", "5m ago", "3h ago", "2d ago", then the plain date after a week. */
export const formatRelative = (value, empty = '—') => {
  const date = toDate(value);
  if (!date) return empty;
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

/** "45s", "12m 5s", "1h 3m" — zero parts are left out. */
export const formatDuration = (seconds, empty = '—') => {
  const total = Math.round(Number(seconds) || 0);
  if (total <= 0) return empty;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return [hours && `${hours}h`, minutes && `${minutes}m`, secs && `${secs}s`].filter(Boolean).join(' ');
};

/** Time passed since `start` (Date, timestamp or ISO string), e.g. "3m 12s". */
export const formatElapsedSince = (start) =>
  formatDuration(Math.max(0, Math.floor((Date.now() - new Date(start).getTime()) / 1000)), '0s');

/** A list of names as "A, B, C"; anything else is returned as is. */
export const joinNames = (value) => (Array.isArray(value) ? value.join(', ') : value);

export const plural = (count, word) => `${count} ${count === 1 ? word : `${word}s`}`;

const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

/** True when two labels read the same (ignores case and whitespace). */
export const sameText = (a, b) => normalizeText(a) === normalizeText(b);
